// Reporting — Analytics tab styled after Smarter Contact's reporting UX.
// Two sub-tabs: Messaging + Calling. Every KPI card is fully wired to
// backend aggregations (/api/reports/messaging, /api/reports/calling,
// /api/reports/pipeline) so nothing here is a mock.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card, CardContent,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
  Mail, MailPlus, MessageSquareOff, ShieldOff, CheckCircle2, XCircle,
  Flame, Users, Clock, Sparkles, MessageSquareReply,
  PhoneCall, PhoneIncoming, MessageCircle, Timer,
  TrendingUp, Trophy, Percent, Download, RefreshCw,
} from "lucide-react";

// -------- helpers --------
const fmtInt = (n) => (n == null ? "0" : Number(n).toLocaleString());
const fmtPct = (n) => (n == null ? "0.00%" : `${Number(n).toFixed(2)}%`);
const fmtDuration = (s) => {
  const sec = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return `${m}m ${String(r).padStart(2, "0")}s`;
};
const toISODate = (d) => d.toISOString().slice(0, 10);

// KPI card — icon + label + value. Uses subtle grey icon disc like Smarter.
function KpiCard({ Icon, label, value, tone = "slate", testid }) {
  const iconBg = {
    slate:   "bg-slate-100 text-slate-500",
    emerald: "bg-emerald-100 text-emerald-600",
    sky:     "bg-sky-100 text-sky-600",
    violet:  "bg-violet-100 text-violet-600",
    amber:   "bg-amber-100 text-amber-600",
    rose:    "bg-rose-100 text-rose-600",
    orange:  "bg-orange-100 text-orange-600",
  }[tone];
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white"
      data-testid={testid}
    >
      <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-slate-500 leading-tight whitespace-pre-line">{label}</div>
        <div className="font-semibold text-lg leading-tight" data-testid={`${testid}-value`}>{value}</div>
      </div>
    </div>
  );
}

// Section wrapper
function StatSection({ title, testid, right = null, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-widest">
          {title}
        </h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function Reporting() {
  const [tab, setTab] = useState("messaging"); // "messaging" | "calling"

  // Date range — default last 30 days
  const [end, setEnd] = useState(() => toISODate(new Date()));
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return toISODate(d);
  });
  const [campaignId, setCampaignId] = useState(""); // "" = all

  const [msgData, setMsgData]   = useState(null);
  const [callData, setCallData] = useState(null);
  const [pipeData, setPipeData] = useState(null);
  const [loading, setLoading]   = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (start) qs.set("start", start);
      if (end)   qs.set("end", end);
      if (campaignId) qs.set("campaign_id", campaignId);
      const [m, c, p] = await Promise.all([
        api.get(`/reports/messaging?${qs.toString()}`),
        api.get(`/reports/calling?${qs.toString()}`),
        api.get(`/reports/pipeline?${qs.toString()}`),
      ]);
      setMsgData(m.data);
      setCallData(c.data);
      setPipeData(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [start, end, campaignId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const s = msgData?.stats || {};
  const cs = callData?.stats || {};
  const pf = pipeData?.funnel || {};
  const rev = pipeData?.revenue || {};

  const conversionSteps = useMemo(() => {
    const steps = [
      { key: "leads",        label: "Leads",        value: pf.leads || 0 },
      { key: "contacts",     label: "Contacts",     value: pf.contacts || 0 },
      { key: "appointments", label: "Appointments", value: pf.appointments || 0 },
      { key: "contracts",    label: "Contracts",    value: pf.contracts || 0 },
      { key: "closed",       label: "Closed",       value: pf.closed || 0 },
    ];
    const max = Math.max(...steps.map((s) => s.value), 1);
    return steps.map((s) => ({ ...s, pct: Math.round((s.value / max) * 100) }));
  }, [pf]);

  const exportCsv = () => {
    const rows = [];
    if (tab === "messaging") {
      rows.push(["Metric", "Value"]);
      Object.entries(s).forEach(([k, v]) => rows.push([k, v]));
    } else {
      rows.push(["Metric", "Value"]);
      Object.entries(cs).forEach(([k, v]) => rows.push([k, v]));
    }
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `report-${tab}-${start}-to-${end}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="reporting-page">
      {/* Header + Tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Reporting</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every message, every dial — one dashboard.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadAll}
            disabled={loading}
            data-testid="reporting-refresh-btn"
            className="rounded-full"
          >
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={exportCsv}
            data-testid="reporting-export-btn"
            className="rounded-full"
          >
            <Download size={14} className="mr-1.5" />
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-6">
          {[
            { key: "messaging", label: "Messaging" },
            { key: "calling",   label: "Calling"   },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`reporting-tab-${t.key}`}
              className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-slate-800 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <StatSection
        title={tab === "messaging" ? "Message reporting" : "Call reporting"}
        testid="reporting-filters-section"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-500">Date range</Label>
            <div className="flex gap-2 items-center mt-1">
              <Input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                data-testid="reporting-start-date"
                className="h-9 text-sm"
              />
              <span className="text-slate-400">–</span>
              <Input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="reporting-end-date"
                className="h-9 text-sm"
              />
            </div>
          </div>
          {tab === "messaging" && (
            <div>
              <Label className="text-xs text-slate-500">Campaigns</Label>
              <select
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-white px-3 text-sm"
                data-testid="reporting-campaign-select"
              >
                <option value="">All</option>
                {(msgData?.campaigns_available || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </StatSection>

      {/* Statistics */}
      {tab === "messaging" ? (
        <StatSection title="Statistics" testid="reporting-messaging-stats">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
              <div className="text-xs text-slate-500">Standard campaigns</div>
              <div className="text-2xl font-semibold text-slate-800 mt-1"
                   data-testid="reporting-standard-campaigns-value">
                {fmtInt(s.standard_campaigns)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
              <div className="text-xs text-slate-500">Keyword campaigns</div>
              <div className="text-2xl font-semibold text-slate-800 mt-1"
                   data-testid="reporting-keyword-campaigns-value">
                {fmtInt(s.keyword_campaigns)}
              </div>
            </div>
            <div className="rounded-xl border border-emerald-200 p-4 bg-emerald-50">
              <div className="text-xs text-emerald-700">Leads (from replies)</div>
              <div className="text-2xl font-semibold text-emerald-800 mt-1"
                   data-testid="reporting-leads-value">
                {fmtInt(s.leads)}
              </div>
            </div>
            <div className="rounded-xl border border-sky-200 p-4 bg-sky-50">
              <div className="text-xs text-sky-700">Contacts</div>
              <div className="text-2xl font-semibold text-sky-800 mt-1"
                   data-testid="reporting-contacts-value">
                {fmtInt(s.contacts)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left column — volume + rates */}
            <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-2">
              <KpiCard Icon={Mail}              tone="slate"   label="SMS sent"          value={fmtInt(s.sms_sent)}          testid="kpi-sms-sent" />
              <KpiCard Icon={MailPlus}          tone="slate"   label="SMS segments sent" value={fmtInt(s.sms_sent)}          testid="kpi-sms-segments" />
              <KpiCard Icon={ShieldOff}         tone="rose"    label="Carrier block rate" value={fmtPct(s.carrier_block_rate)} testid="kpi-block-rate" />
              <KpiCard Icon={MessageSquareReply} tone="emerald" label="Replies received"  value={fmtInt(s.replies_received)}  testid="kpi-replies" />
              <KpiCard Icon={CheckCircle2}      tone="emerald" label="Delivery rate"     value={fmtPct(s.delivery_rate)}     testid="kpi-delivery-rate" />
              <KpiCard Icon={MessageSquareOff}  tone="amber"   label="Opt-out rate"      value={fmtPct(s.opt_out_rate)}      testid="kpi-opt-out-rate" />
              <KpiCard Icon={Sparkles}          tone="violet"  label="AI filtering rate" value={fmtPct(s.ai_filter_rate)}    testid="kpi-ai-filter-rate" />
              <KpiCard Icon={MessageCircle}     tone="sky"     label="Reply rate"        value={fmtPct(s.reply_rate)}        testid="kpi-reply-rate" />
            </div>

            {/* Right column — conversion + response */}
            <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-2">
              <KpiCard Icon={Clock}    tone="slate"  label={"Median response\ntime"}          value={s.median_response_min ? `${s.median_response_min}m` : "—"} testid="kpi-median-response" />
              <KpiCard Icon={Flame}    tone="orange" label="Leads"                            value={fmtInt(s.leads)}                                    testid="kpi-leads" />
              <KpiCard Icon={Users}    tone="sky"    label="Contacts"                         value={fmtInt(s.contacts)}                                 testid="kpi-contacts" />
              <KpiCard Icon={TrendingUp} tone="emerald" label={"SMS to lead\nconversion rate"} value={fmtPct(s.sms_to_lead_rate)}                        testid="kpi-sms-to-lead" />
              <KpiCard Icon={Percent}  tone="violet" label={"Contact to lead\nconversion rate"} value={fmtPct(s.contact_to_lead_rate)}                  testid="kpi-contact-to-lead" />
            </div>
          </div>
        </StatSection>
      ) : (
        <StatSection title="Statistics" testid="reporting-calling-stats">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-2">
              <KpiCard Icon={PhoneCall}     tone="slate"   label="Total calls dialed"    value={fmtInt(cs.total_calls_dialed)}    testid="kpi-calls-dialed" />
              <KpiCard Icon={PhoneIncoming} tone="slate"   label="Total calls answered"  value={fmtInt(cs.total_calls_answered)}  testid="kpi-calls-answered" />
              <KpiCard Icon={MessageCircle} tone="slate"   label="Conversations"         value={fmtInt(cs.conversations)}         testid="kpi-conversations" />
              <KpiCard Icon={Percent}       tone="emerald" label="Response rate"         value={fmtPct(cs.response_rate)}         testid="kpi-response-rate" />
              <KpiCard Icon={Timer}         tone="sky"     label="Average call time"     value={fmtDuration(cs.avg_call_seconds)} testid="kpi-avg-call-time" />
              <KpiCard Icon={Percent}       tone="violet"  label="Conversations rate"    value={fmtPct(cs.conversation_rate)}     testid="kpi-conv-rate" />
            </div>
            <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-2">
              <KpiCard Icon={Flame}       tone="orange"  label="Leads"                          value={fmtInt(cs.leads)}                testid="kpi-call-leads" />
              <KpiCard Icon={Trophy}      tone="amber"   label="Deals closed"                   value={fmtInt(cs.deals_closed)}         testid="kpi-deals-closed" />
              <KpiCard Icon={TrendingUp}  tone="emerald" label={"Call to lead\nconversion rate"} value={fmtPct(cs.call_to_lead_rate)}    testid="kpi-call-to-lead" />
              <KpiCard Icon={Percent}     tone="violet"  label={"Lead to deal\nconversion rate"} value={fmtPct(cs.lead_to_deal_rate)}    testid="kpi-lead-to-deal" />
            </div>
          </div>
        </StatSection>
      )}

      {/* Pipeline funnel — shown regardless of tab, users love it */}
      <StatSection title="Pipeline funnel" testid="reporting-pipeline-section">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {conversionSteps.map((step) => (
            <div
              key={step.key}
              className="rounded-xl border border-slate-200 p-4 bg-white relative overflow-hidden"
              data-testid={`funnel-${step.key}`}
            >
              <div
                className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-emerald-500 to-sky-500"
                style={{ width: `${step.pct}%` }}
              />
              <div className="text-xs text-slate-500">{step.label}</div>
              <div className="text-2xl font-semibold text-slate-800 mt-1">
                {fmtInt(step.value)}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-slate-500">Total estimated revenue</div>
              <div className="text-2xl font-semibold text-emerald-700 mt-1"
                   data-testid="reporting-revenue-total">
                ${fmtInt(Math.round(rev.total_estimated || 0))}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Signed offers · assignment fees or 10% of ARV-spread estimate
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-slate-500">Avg per closed deal</div>
              <div className="text-2xl font-semibold text-slate-800 mt-1"
                   data-testid="reporting-revenue-avg">
                ${fmtInt(Math.round(rev.avg_per_deal || 0))}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Simple mean across closed offers in this window
              </div>
            </CardContent>
          </Card>
        </div>
      </StatSection>
    </div>
  );
}
